// client/src/components/pages/LexiStar/Replay/replayMulti.ts
//
// ST-00277 — Replay of several trucks at once, as pure functions (./ReplayMulti renders them;
// the tests pin them without a map):
//   - the address: `asset_keys=k1,k2,…` (the trucks, in their colour order) and `hide=…` (the
//     trucks whose track is switched off), so a multi-truck replay can be shared and bookmarked;
//   - the picker's selection: select all (up to MAX_REPLAY_TRUCKS), toggle, the options' order;
//   - the limits (./replayLimits, a byte-for-byte copy of the server's replay/limits.ts, so the
//     page and GET /replay/batch agree exactly, ST-00311): up to 100 trucks; 7 days up to 25
//     trucks, shorter past that (trucks x hours within 25 x 7 days);
//   - GET /replay/batch normalised: one `MultiTruck` per replayed truck (its lighter body through
//     replayLogic.normaliseReplay), the skipped keys apart;
//   - colour: each truck its own colour from the shared categorical palette (the Vehicle
//     Tracker's trip palette, ./trips/tripsDay TRIP_PALETTE, one step per basemap); the
//     puck keeps the status colour of the organisation's display rules (./replayDisplay), so the
//     line says WHICH truck and the puck WHAT it is doing. Eight hues repeat past eight trucks, so
//     colour is never the only cue: every puck carries its registration, the legend and the lanes
//     name each truck.
//   - the map's sources: every visible truck's track in one line collection, every visible
//     truck's events in one point collection (a circle layer: thousands of DOM markers would not
//     keep up with a replay of many trucks).

import type { Feature, FeatureCollection, LineString, Point } from 'geojson';
import { TRIP_CASING, TRIP_PALETTE, inkOn, type Basemap } from './trips/tripsDay';
import { normaliseReplay, registrationOf, toMs, type ReplayData, type ReplayPosition } from './replayLogic';
import type {
  ReplayBatchAssetDto,
  ReplayBatchLimitsDto,
  ReplayBatchResponse,
  ReplayBatchSkippedDto,
} from '../../../../types/lexistar-replay';
import {
  FULL_RANGE_TRUCKS,
  MAX_MULTI_RANGE_HOURS,
  MAX_REPLAY_DISPLAY_KEYS,
  MAX_REPLAY_TRUCKS,
  maxMultiRangeHours,
  maxMultiRangeMs,
  maxTrucksFor,
  rangeWords,
} from './replayLimits';

export type { Basemap };
export { inkOn };

// The most trucks one replay shows (100: the server's MAX_BATCH_ASSETS, one source; ST-00311),
// and how long a replay of so many trucks covers.
export { MAX_REPLAY_TRUCKS, FULL_RANGE_TRUCKS, maxMultiRangeHours, rangeWords };
/** The longest window any replay of several trucks covers (one truck alone: 31 days). */
export const MAX_MULTI_RANGE_MS = MAX_MULTI_RANGE_HOURS * 3600000;
export const MAX_MULTI_RANGE_DAYS = MAX_MULTI_RANGE_HOURS / 24;

const KEY_RE = /^(reg|asset|device|ext):[^\s/,]{1,100}$/;

// ── The address ─────────────────────────────────────────────────────────────────────────────

/** `asset_keys` / `hide` → the distinct, well-formed keys in order (at most `max`). */
export function parseAssetKeys(v: string | null | undefined, max = MAX_REPLAY_TRUCKS): string[] {
  const out: string[] = [];
  for (const raw of String(v || '').split(',')) {
    const k = raw.trim();
    if (k && KEY_RE.test(k) && !out.includes(k)) out.push(k);
    if (out.length >= max) break;
  }
  return out;
}

/** Keys → the comma list the address carries. */
export const assetKeysParam = (keys: readonly string[]): string => keys.join(',');

// ── The picker's selection ──────────────────────────────────────────────────────────────────

/** `keys` in the order of `order` (keys not in it first, as they came). */
export function orderLike(keys: readonly string[], order: readonly string[]): string[] {
  const pos = new Map(order.map((k, i) => [k, i]));
  const extra = keys.filter((k) => !pos.has(k));
  const known = keys.filter((k) => pos.has(k)).sort((a, b) => (pos.get(a) as number) - (pos.get(b) as number));
  return [...extra, ...known];
}

/**
 * "Select all": the selection plus every candidate (the options shown), up to `max` in all.
 * `capped` when some candidates were left out for the limit.
 */
export function selectAllWithin(
  selected: readonly string[],
  candidates: readonly string[],
  max = MAX_REPLAY_TRUCKS
): { keys: string[]; capped: boolean } {
  const keys = [...selected];
  let capped = false;
  for (const c of candidates) {
    if (keys.includes(c)) continue;
    if (keys.length >= max) {
      capped = true;
      break;
    }
    keys.push(c);
  }
  return { keys, capped };
}

/** "Deselect all": the selection less the candidates shown (all of them without a search). */
export const deselectWithin = (selected: readonly string[], candidates: readonly string[]): string[] =>
  selected.filter((k) => !candidates.includes(k));

/** One truck on or off; `refused` when adding it would pass `max`. */
export function toggleKey(
  selected: readonly string[],
  key: string,
  max = MAX_REPLAY_TRUCKS
): { keys: string[]; refused: boolean } {
  if (selected.includes(key)) return { keys: selected.filter((k) => k !== key), refused: false };
  if (selected.length >= max) return { keys: [...selected], refused: true };
  return { keys: [...selected, key], refused: false };
}

/**
 * Why the Replay button stays grey for this selection and window, or null. Several trucks cover
 * at most 7 days; past 25 trucks less (ST-00311): the note names the count's own limit and how
 * many trucks the chosen window fits.
 */
export function multiFormProblem(count: number, fromMs: number, toMs: number): string | null {
  if (count > MAX_REPLAY_TRUCKS) return `Choose at most ${MAX_REPLAY_TRUCKS} trucks.`;
  if (count < 2 || !Number.isFinite(fromMs) || !Number.isFinite(toMs)) return null;
  const span = toMs - fromMs;
  if (span <= maxMultiRangeMs(count)) return null;
  if (count <= FULL_RANGE_TRUCKS || span > MAX_MULTI_RANGE_MS) {
    return `A replay of several trucks covers at most ${MAX_MULTI_RANGE_DAYS} days. Shorten the time range, or choose one truck.`;
  }
  return (
    `A replay of ${count} trucks covers at most ${rangeWords(maxMultiRangeHours(count))}. ` +
    `Shorten the time range, or choose fewer trucks (up to ${maxTrucksFor(span)} for this time range).`
  );
}

// ── GET /replay/batch ───────────────────────────────────────────────────────────────────────

export interface MultiTruck {
  /** The asset key as asked (the address); the truck's identity on the page. */
  key: string;
  /** 0-based position: the colour slot. */
  index: number;
  /** The registration (else the key without its kind). */
  label: string;
  fleetNo: string | null;
  /** The lighter body, normalised (track, events, stage bands, deviations, trips). */
  replay: ReplayData;
  /** Every key the engine may have written for the truck (its events before the window). */
  keys: string[];
  /** No positions in the window. */
  empty: boolean;
  /** The simplification tolerance the server used (raised past 25 m to fit the batch). */
  toleranceM: number | null;
  /** The truck's read hit its share of the batch: its track stops at `nextFrom`. */
  truncated: boolean;
  nextFrom: number | null;
}

export interface MultiSkipped {
  key: string;
  label: string;
  reason: string;
  message: string;
}

export interface MultiBatch {
  trucks: MultiTruck[];
  skipped: MultiSkipped[];
  limits: ReplayBatchLimitsDto | null;
  /** Fixes read and points drawn across the trucks. */
  points: { read: number; returned: number };
}

const isOk = (a: ReplayBatchAssetDto | ReplayBatchSkippedDto): a is ReplayBatchAssetDto => a.status === 'ok';

export function normaliseBatch(body: ReplayBatchResponse | null | undefined): MultiBatch {
  const trucks: MultiTruck[] = [];
  const skipped: MultiSkipped[] = [];
  for (const item of body?.assets || []) {
    if (!item || typeof item.asset_key !== 'string') continue;
    if (!isOk(item)) {
      skipped.push({
        key: item.asset_key,
        label: registrationOf(item.asset_key),
        reason: item.reason,
        message:
          item.reason === 'duplicate'
            ? 'the same truck is already in the list'
            : 'not found, or not one of your organisation’s trucks with positions in this time range',
      });
      continue;
    }
    const replay = normaliseReplay(item);
    const nextFrom = toMs(item.next_from);
    trucks.push({
      key: item.asset_key,
      index: trucks.length,
      label: replay.asset?.registration || registrationOf(item.asset_key),
      fleetNo: replay.asset?.fleet_no || null,
      replay,
      keys: item.asset_keys && item.asset_keys.length ? item.asset_keys : [item.asset_key],
      empty: replay.track.length === 0,
      toleranceM: typeof item.tolerance_m === 'number' ? item.tolerance_m : null,
      truncated: !!item.truncated,
      nextFrom: Number.isFinite(nextFrom) ? nextFrom : null,
    });
  }
  const read = Number(body?.stats?.points?.read);
  const returned = Number(body?.stats?.points?.returned);
  return {
    trucks,
    skipped,
    limits: body?.limits || null,
    points: {
      read: Number.isFinite(read) ? read : 0,
      returned: Number.isFinite(returned) ? returned : trucks.reduce((s, t) => s + t.replay.track.length, 0),
    },
  };
}

/** The events before the window that belong to this truck (the display rules' look-back). */
export function eventsBeforeOf(
  eventsBefore: ReadonlyArray<{ asset_key?: string; type: string; at: string }>,
  truck: Pick<MultiTruck, 'keys'>
): Array<{ type: string; at: string }> {
  const keys = new Set(truck.keys);
  return eventsBefore.filter((e) => !e.asset_key || keys.has(e.asset_key)).map((e) => ({ type: e.type, at: e.at }));
}

/**
 * The keys to ask the display rules' versions endpoint for: up to 4 per truck, fewer with many
 * trucks so every truck keeps its first ones (2 each at 100 trucks), MAX_REPLAY_DISPLAY_KEYS (200)
 * in all, the server's cap (one source, ./replayLimits).
 */
export function displayKeysOf(trucks: readonly Pick<MultiTruck, 'keys'>[]): string[] {
  const per = Math.max(1, Math.min(4, Math.floor(MAX_REPLAY_DISPLAY_KEYS / Math.max(1, trucks.length))));
  const out = new Set<string>();
  for (const t of trucks) for (const k of t.keys.slice(0, per)) out.add(k);
  return [...out].slice(0, MAX_REPLAY_DISPLAY_KEYS);
}

// ── Colour ──────────────────────────────────────────────────────────────────────────────────

/** A truck's own colour (its 0-based slot) on that basemap. */
export const truckColor = (index: number, basemap: Basemap): string => {
  const palette = TRIP_PALETTE[basemap];
  return palette[((index % palette.length) + palette.length) % palette.length] as string;
};

/** The casing under every track: dark on the street map, light on imagery. */
export const truckCasing = (basemap: Basemap): string => TRIP_CASING[basemap];

// ── The map's sources ───────────────────────────────────────────────────────────────────────

/** Every visible truck's track, one LineString each, with its colour. */
export function multiTrackFeatures(
  trucks: readonly MultiTruck[],
  hidden: ReadonlySet<string>,
  basemap: Basemap
): FeatureCollection<LineString, { key: string; color: string }> {
  const features: Feature<LineString, { key: string; color: string }>[] = [];
  for (const t of trucks) {
    if (hidden.has(t.key) || t.replay.track.length < 2) continue;
    features.push({
      type: 'Feature',
      properties: { key: t.key, color: truckColor(t.index, basemap) },
      geometry: { type: 'LineString', coordinates: t.replay.track.map((p) => [p.lon, p.lat]) },
    });
  }
  return { type: 'FeatureCollection', features };
}

/** Every visible truck's events with a position, as points (a circle layer). */
export function multiEventFeatures(
  trucks: readonly MultiTruck[],
  hidden: ReadonlySet<string>
): FeatureCollection<Point, { key: string; type: string; t: number }> {
  const features: Feature<Point, { key: string; type: string; t: number }>[] = [];
  for (const t of trucks) {
    if (hidden.has(t.key)) continue;
    for (const e of t.replay.events) {
      const lat = Number(e.lat);
      const lon = Number(e.lon);
      if (e.lat === null || e.lat === undefined || e.lon === null || e.lon === undefined) continue;
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
      features.push({
        type: 'Feature',
        properties: { key: t.key, type: String(e.type || ''), t: e.t },
        geometry: { type: 'Point', coordinates: [lon, lat] },
      });
    }
  }
  return { type: 'FeatureCollection', features };
}

/**
 * A lane's event marks: at most one per `buckets`-th of the window (a replay of many trucks may
 * carry thousands of events; one mark per bucket reads the same and keeps the DOM small).
 */
export function laneEventTimes(
  events: readonly { t: number }[],
  start: number,
  end: number,
  buckets = 300
): number[] {
  const span = end - start;
  if (!(span > 0)) return [];
  const seen = new Set<number>();
  const out: number[] = [];
  for (const e of events) {
    if (!Number.isFinite(e.t) || e.t < start || e.t > end) continue;
    const b = Math.min(buckets - 1, Math.floor(((e.t - start) / span) * buckets));
    if (seen.has(b)) continue;
    seen.add(b);
    out.push(e.t);
  }
  return out;
}

/** Every truck's event times, merged and sorted (⏮ / ⏭ jump across the trucks). */
export function mergedEventTimes(trucks: readonly Pick<MultiTruck, 'replay'>[]): { t: number }[] {
  return trucks
    .flatMap((t) => t.replay.events.map((e) => e.t))
    .filter((t) => Number.isFinite(t))
    .sort((a, b) => a - b)
    .map((t) => ({ t }));
}

/** [[w, s], [e, n]] around every truck's track (null without positions). */
export function multiBounds(trucks: readonly Pick<MultiTruck, 'replay'>[]): [[number, number], [number, number]] | null {
  let w = Infinity;
  let s = Infinity;
  let e = -Infinity;
  let n = -Infinity;
  for (const t of trucks) {
    for (const p of t.replay.track) {
      if (p.lon < w) w = p.lon;
      if (p.lon > e) e = p.lon;
      if (p.lat < s) s = p.lat;
      if (p.lat > n) n = p.lat;
    }
  }
  if (!Number.isFinite(w)) return null;
  // A single point (or a parked truck): a small box around it.
  const pad = 0.002;
  return [
    [w - (e - w < pad ? pad : 0), s - (n - s < pad ? pad : 0)],
    [e + (e - w < pad ? pad : 0), n + (n - s < pad ? pad : 0)],
  ];
}

// ── Words ───────────────────────────────────────────────────────────────────────────────────

/** A position's motion as the card and the list say it. */
export function motionWords(p: Pick<ReplayPosition, 'motion'> | null | undefined): string {
  if (!p) return 'no position at this time';
  if (p.motion === 'moving') return 'moving';
  if (p.motion === 'stationary') return 'standing';
  return 'no position at this time';
}

/** "3 trucks", "1 truck". */
export const trucksWord = (n: number): string => `${n} truck${n === 1 ? '' : 's'}`;
